import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LogOut, Download, RefreshCw, Mail, Phone, Inbox, Handshake, Search } from "lucide-react";
import { useAuth, authHeaders } from "@/context/AuthContext";
import { ASSETS } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const toCsv = (rows, headers) => {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows.map((r) => headers.map((h) => esc(r[h.key])).join(",")).join("\n");
  return head + "\n" + body;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const TabButton = ({ active, onClick, children, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-5 py-2.5 text-sm font-semibold border transition-colors flex items-center gap-2 ${active ? "bg-[var(--ptt-ink)] text-white border-[var(--ptt-ink)]" : "bg-white text-[var(--ptt-ink)] border-[var(--ptt-line)] hover:border-[var(--ptt-ink)]"}`}
  >
    {children}
    {count != null && (
      <span className={`font-mono-tech text-[10px] px-1.5 py-0.5 ${active ? "bg-[var(--ptt-teal)] text-[var(--ptt-ink)]" : "bg-[var(--ptt-paper)] border border-[var(--ptt-line)]"}`}>{count}</span>
    )}
  </button>
);

const Admin = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("contact");
  const [contacts, setContacts] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    setFetching(true);
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/admin/contact`, { headers: authHeaders() }),
        axios.get(`${API}/admin/resellers`, { headers: authHeaders() }),
      ]);
      setContacts(c.data);
      setResellers(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        await logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load submissions.");
      }
    } finally {
      setFetching(false);
    }
  }, [logout, navigate]);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  const filtered = useMemo(() => {
    const list = tab === "contact" ? contacts : resellers;
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [tab, query, contacts, resellers]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const exportCsv = () => {
    if (tab === "contact") {
      const headers = [
        { key: "submitted_at", label: "Submitted" }, { key: "name", label: "Name" }, { key: "email", label: "Email" },
        { key: "phone", label: "Phone" }, { key: "company", label: "Company" }, { key: "interest", label: "Interest" }, { key: "message", label: "Message" },
      ];
      downloadCsv(`pttnz-contact-${new Date().toISOString().slice(0,10)}.csv`, toCsv(filtered, headers));
    } else {
      const headers = [
        { key: "submitted_at", label: "Submitted" }, { key: "company", label: "Company" }, { key: "contact_name", label: "Contact" },
        { key: "email", label: "Email" }, { key: "phone", label: "Phone" }, { key: "region", label: "Region" },
        { key: "business_type", label: "Business type" }, { key: "monthly_volume", label: "Volume" }, { key: "notes", label: "Notes" },
      ];
      downloadCsv(`pttnz-resellers-${new Date().toISOString().slice(0,10)}.csv`, toCsv(filtered, headers));
    }
    toast.success("CSV downloaded.");
  };

  return (
    <div className="min-h-screen bg-[var(--ptt-paper)]" data-testid="admin-page">
      <Toaster position="top-right" richColors />
      {/* Top bar */}
      <header className="bg-[var(--ptt-ink)] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-link">
            <img src={ASSETS.logo} alt="PTT NZ" className="h-10 w-auto" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 hidden sm:inline">Admin · Submissions</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline font-mono-tech text-[11px] text-white/60" data-testid="admin-user-email">{user.email}</span>
            <button onClick={async () => { await logout(); navigate("/admin/login", { replace: true }); }} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm border border-white/20 hover:border-[var(--ptt-teal)] hover:text-[var(--ptt-teal)] px-3 py-1.5 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="eyebrow">Submissions</div>
            <h1 className="font-display text-4xl font-bold mt-2">Inbox</h1>
            <p className="text-sm text-[var(--ptt-muted)] mt-1">Quote requests and reseller applications, latest first.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="admin-toolbar">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ptt-muted)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search"
                placeholder="Search…"
                className="pl-9 pr-3 py-2.5 text-sm border border-[var(--ptt-line)] focus:border-[var(--ptt-ink)] outline-none w-56 bg-white"
              />
            </div>
            <button onClick={fetchAll} data-testid="admin-refresh" disabled={fetching} className="inline-flex items-center gap-1.5 text-sm border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] px-3 py-2.5 bg-white">
              <RefreshCw size={14} className={fetching ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCsv} data-testid="admin-export-csv" className="inline-flex items-center gap-1.5 text-sm border border-[var(--ptt-ink)] bg-[var(--ptt-ink)] text-white px-3 py-2.5 hover:bg-[var(--ptt-teal)] hover:text-[var(--ptt-ink)] hover:border-[var(--ptt-teal)] transition-colors">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4" data-testid="admin-tabs">
          <TabButton active={tab === "contact"} onClick={() => { setTab("contact"); setSelected(null); }} count={contacts.length} testId="admin-tab-contact">
            <Inbox size={14} /> Quote requests
          </TabButton>
          <TabButton active={tab === "resellers"} onClick={() => { setTab("resellers"); setSelected(null); }} count={resellers.length} testId="admin-tab-resellers">
            <Handshake size={14} /> Reseller applications
          </TabButton>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-7 bg-white border border-[var(--ptt-line)]" data-testid="admin-list">
            {fetching && filtered.length === 0 && (
              <div className="p-10 text-center text-sm text-[var(--ptt-muted)]">Loading…</div>
            )}
            {!fetching && filtered.length === 0 && (
              <div className="p-10 text-center text-sm text-[var(--ptt-muted)]" data-testid="admin-empty">No submissions yet.</div>
            )}
            <ul>
              {filtered.map((row) => {
                const isContact = tab === "contact";
                const title = isContact ? row.name : row.company;
                const sub = isContact ? row.email : `${row.contact_name} · ${row.email}`;
                const tagText = isContact ? (row.interest || row.company || "Quote") : (row.region || row.business_type || "Reseller");
                const active = selected && selected.id === row.id;
                return (
                  <li key={row.id}>
                    <button
                      onClick={() => setSelected(row)}
                      data-testid={`admin-row-${row.id}`}
                      className={`w-full text-left px-5 py-4 border-b border-[var(--ptt-line)] last:border-b-0 hover:bg-[var(--ptt-paper)] transition-colors ${active ? "bg-[var(--ptt-paper)] border-l-4 border-l-[var(--ptt-teal)]" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-display font-bold text-sm truncate">{title}</div>
                          <div className="text-xs text-[var(--ptt-muted)] truncate mt-0.5">{sub}</div>
                        </div>
                        <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[var(--ptt-muted)] whitespace-nowrap">{fmtDate(row.submitted_at)}</span>
                      </div>
                      <div className="mt-2 inline-flex items-center text-[10px] font-mono-tech uppercase tracking-widest text-[var(--ptt-ink)] bg-[var(--ptt-teal)]/20 px-2 py-0.5">{tagText}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Detail */}
          <div className="lg:col-span-5">
            {selected ? (
              <div className="bg-white border border-[var(--ptt-ink)] p-6 sticky top-6" data-testid="admin-detail">
                <div className="eyebrow">{tab === "contact" ? "Quote request" : "Reseller application"}</div>
                <h2 className="font-display text-2xl font-bold mt-2">{tab === "contact" ? selected.name : selected.company}</h2>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] mt-1">Received {fmtDate(selected.submitted_at)}</div>

                <dl className="mt-5 space-y-3 text-sm">
                  {tab === "contact" ? (
                    <>
                      <Detail label="Email" value={<a href={`mailto:${selected.email}`} className="underline">{selected.email}</a>} icon={<Mail size={13} />} />
                      <Detail label="Phone" value={selected.phone ? <a href={`tel:${selected.phone}`} className="underline">{selected.phone}</a> : "—"} icon={<Phone size={13} />} />
                      <Detail label="Company" value={selected.company || "—"} />
                      <Detail label="Interest" value={selected.interest || "—"} />
                      <Detail label="Message" value={<span className="whitespace-pre-wrap">{selected.message}</span>} />
                    </>
                  ) : (
                    <>
                      <Detail label="Contact" value={selected.contact_name} />
                      <Detail label="Email" value={<a href={`mailto:${selected.email}`} className="underline">{selected.email}</a>} icon={<Mail size={13} />} />
                      <Detail label="Phone" value={selected.phone || "—"} icon={<Phone size={13} />} />
                      <Detail label="Region" value={selected.region || "—"} />
                      <Detail label="Business type" value={selected.business_type || "—"} />
                      <Detail label="Monthly volume" value={selected.monthly_volume || "—"} />
                      <Detail label="Notes" value={selected.notes ? <span className="whitespace-pre-wrap">{selected.notes}</span> : "—"} />
                    </>
                  )}
                </dl>

                <div className="mt-6 flex gap-2 flex-wrap">
                  <a href={`mailto:${selected.email}`} className="ptt-button px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5" data-testid="admin-reply-email"><Mail size={12} /> Reply by email</a>
                  {selected.phone && <a href={`tel:${selected.phone}`} className="ghost-button px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5"><Phone size={12} /> Call</a>}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-dashed border-[var(--ptt-line)] p-10 text-center text-sm text-[var(--ptt-muted)]" data-testid="admin-detail-empty">
                Select a submission to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Detail = ({ label, value, icon }) => (
  <div>
    <dt className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] flex items-center gap-1.5">{icon}{label}</dt>
    <dd className="mt-1 text-sm text-[var(--ptt-ink)]">{value}</dd>
  </div>
);

export default Admin;
